import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Users, Award, Ticket, Mail, Trophy, Download, MailPlus, ShieldCheck, Trash2 } from "lucide-react";
import { MaskedEmail } from "@/components/MaskedEmail";

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/members");
      setMembers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (m) =>
        (m.name || "").toLowerCase().includes(term) ||
        (m.email || "").toLowerCase().includes(term)
    );
  }, [q, members]);

  const totalWins = useMemo(
    () => members.reduce((acc, m) => acc + (m.raffles_won?.length || 0), 0),
    [members]
  );

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");

  const [busy, setBusy] = useState(null);

  const resendVerification = async (m) => {
    setBusy(m.id);
    try {
      await api.post(`/admin/members/${m.id}/resend-verification`);
      toast.success(`Verification email resent to ${m.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const manuallyVerify = async (m) => {
    if (!window.confirm(`Mark ${m.email} as verified? They'll be able to log in immediately.`)) return;
    setBusy(m.id);
    try {
      await api.post(`/admin/members/${m.id}/verify`);
      toast.success(`${m.email} marked as verified`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const deleteMember = async (m) => {
    if (!window.confirm(`Permanently delete the account ${m.email}? This cannot be undone.`)) return;
    setBusy(m.id);
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success(`${m.email} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="admin-members">
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Customers</div>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Members</h1>
          <p className="text-white/50 text-sm mt-2 max-w-2xl">
            View and search registered members, how many raffles they've entered, which they've won, and their contact email.
            Access is restricted to admins and used solely to administer competitions (UK GDPR — legitimate interest / contract).
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            onClick={() => download("/admin/export/members", `ufr-members-${new Date().toISOString().slice(0, 10)}.csv`)}
            className="btn-ufr-ghost px-4 py-2.5 text-xs inline-flex items-center gap-2"
            data-testid="export-members-csv-btn"
          >
            <Download size={13} /> Members CSV
          </button>
          <button
            onClick={() => download("/admin/export/entries", `ufr-entries-${new Date().toISOString().slice(0, 10)}.csv`)}
            className="btn-ufr-ghost px-4 py-2.5 text-xs inline-flex items-center gap-2"
            data-testid="export-entries-csv-btn"
          >
            <Download size={13} /> Entries CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <Stat icon={<Users size={16} />} label="Members" value={members.length} />
        <Stat icon={<Ticket size={16} />} label="Total tickets" value={members.reduce((a, m) => a + (m.total_tickets || 0), 0)} />
        <Stat icon={<Trophy size={16} />} label="Total wins" value={totalWins} />
      </div>

      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          className="w-full bg-black/50 border border-white/15 rounded-sm pl-10 pr-3 py-3 text-sm text-white focus:border-[color:var(--ufr-green)] outline-none"
          placeholder="Search by name or email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="members-search-input"
        />
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="ufr-card rounded-md p-10 text-center" data-testid="members-empty">
          <Users className="mx-auto text-white/30" size={32} />
          <div className="font-display text-2xl text-white mt-3">{members.length === 0 ? "No members yet" : "No matches"}</div>
          <p className="text-white/60 text-sm mt-2">
            {members.length === 0 ? "Members appear here once people sign up." : "Try a different name or email."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="members-list">
          {filtered.map((m) => (
            <div key={m.id} className="ufr-card rounded-md p-4 grid grid-cols-12 gap-4 items-center" data-testid={`member-row-${m.id}`}>
              <div className="col-span-12 sm:col-span-4">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg text-white">{m.name || "—"}</div>
                  {m.role === "admin" && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-[rgba(34,255,102,0.12)] text-[color:var(--ufr-green)]">Admin</span>
                  )}
                  {m.email_verified === false && (
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm bg-amber-400/10 text-amber-300" data-testid={`member-unverified-${m.id}`}>Unverified</span>
                  )}
                </div>
                <div className="text-xs text-white/60 flex items-center gap-1 mt-1">
                  <Mail size={11} /> <MaskedEmail email={m.email} testid={`member-email-${m.id}`} />
                </div>
                <div className="text-[11px] text-white/35 mt-1">Joined {fmtDate(m.created_at)}</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {m.email_verified === false && (
                    <>
                      <button
                        onClick={() => resendVerification(m)}
                        disabled={busy === m.id}
                        className="btn-ufr-ghost px-2.5 py-1 text-[10px] inline-flex items-center gap-1"
                        data-testid={`member-resend-verification-${m.id}`}
                      >
                        <MailPlus size={11} /> {busy === m.id ? "Sending…" : "Resend verification"}
                      </button>
                      <button
                        onClick={() => manuallyVerify(m)}
                        disabled={busy === m.id}
                        className="btn-ufr-ghost px-2.5 py-1 text-[10px] inline-flex items-center gap-1"
                        data-testid={`member-mark-verified-${m.id}`}
                      >
                        <ShieldCheck size={11} /> Mark verified
                      </button>
                    </>
                  )}
                  {m.role !== "admin" && (
                    <button
                      onClick={() => deleteMember(m)}
                      disabled={busy === m.id}
                      className="px-2.5 py-1 text-[10px] inline-flex items-center gap-1 rounded-sm border border-red-400/25 text-red-300/80 hover:text-red-300 hover:border-red-400/50 transition-colors"
                      data-testid={`member-delete-${m.id}`}
                    >
                      <Trash2 size={11} /> Delete account
                    </button>
                  )}
                </div>
              </div>

              <div className="col-span-6 sm:col-span-2 text-center">
                <div className="font-display text-2xl text-white">{m.raffles_entered}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Raffles entered</div>
              </div>
              <div className="col-span-6 sm:col-span-2 text-center">
                <div className="font-display text-2xl text-white">{m.total_tickets}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Tickets</div>
              </div>

              <div className="col-span-12 sm:col-span-4">
                {m.raffles_won && m.raffles_won.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5" data-testid={`member-wins-${m.id}`}>
                    {m.raffles_won.map((w, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-sm bg-yellow-400/10 text-yellow-300" title={w.title}>
                        <Award size={11} /> {w.title}{w.ticket_number ? ` (#${w.ticket_number})` : ""}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-white/35">No wins yet</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="ufr-card rounded-md p-4">
      <div className="flex items-center gap-2 text-white/50 text-[10px] uppercase tracking-wider font-bold">
        <span className="text-[color:var(--ufr-green)]">{icon}</span> {label}
      </div>
      <div className="font-display text-3xl text-white mt-1">{value}</div>
    </div>
  );
}
